<script generics="T" lang="ts">
	import { _ } from '@repo/i18n'
	import { ButtonType } from '@repo/ui'
	import { Button } from '@repo/ui'
	import { type Component, createEventDispatcher, onMount } from 'svelte'
	import { preventDefault } from 'svelte/legacy'
	import { fly } from 'svelte/transition'

	interface StepComponentProps {
		form: T
		loading?: boolean
		onContinue?: () => void
	}

	interface Props {
		steps: Component<StepComponentProps>[]
		loading: boolean
		form: T
		isSubmitted: boolean
		name: string
		children?: import('svelte').Snippet
	}

	let {
		steps,
		loading = false,
		form = $bindable(),
		isSubmitted = $bindable(false),
		name,
		children,
	}: Props = $props()

	let selectedStep = $state(0)

	const dispatch = createEventDispatcher()

	export function onSubmit() {
		dispatch('submit')
	}

	let directionForward = $state(true)

	let notEditableSteps = []

	function changeStep(forward: boolean) {
		directionForward = forward
		selectedStep = selectedStep + (forward ? 1 : -1)
		document.body.scrollIntoView()
	}

	function backStep() {
		if (selectedStep > 0) {
			changeStep(false)
		}
	}

	function continueStep() {
		if (!isLastStep) {
			changeStep(true)
		} else {
			onSubmit()
			deleteFormBackup()
		}
	}

	function handleSubmit() {
		if (typeof stepComponent.handleSubmit === 'function') {
			stepComponent.handleSubmit()
		} else {
			continueStep()
		}
	}

	let backupTimer: ReturnType<typeof setTimeout>

	function backupForm() {
		clearTimeout(backupTimer)

		backupTimer = setTimeout(() => {
			localStorage.setItem(name, JSON.stringify(form))
		}, 1000)
	}

	function restoreForm() {
		const savedForm = localStorage.getItem(name)
		if (savedForm) {
			const savedFormData = JSON.parse(savedForm)
			form = { ...form, ...savedFormData }
		}
	}

	export function deleteFormBackup() {
		localStorage.removeItem(name)
	}

	onMount(restoreForm)

	let stepComponent: any = $state(null)

	let stepsCount = $derived(steps.length)
	let isLastStep = $derived(selectedStep === stepsCount - 1)
	let currentStepComponent = $derived(steps[selectedStep])
	let isPreviousStep = $derived(
		selectedStep > 0 && !notEditableSteps.includes(steps[selectedStep - 1]),
	)
</script>

{#if !isSubmitted}
	<form {name} oninput={backupForm} onsubmit={preventDefault(handleSubmit)}>
		{#each steps as step, index (index)}
			{#if currentStepComponent === step}
				{@const SvelteComponent = step}
				<div in:fly|local={{ x: directionForward ? 200 : -200, duration: 200 }}>
					<SvelteComponent
						bind:this={stepComponent}
						onContinue={continueStep}
						bind:form
						bind:loading
					/>
				</div>
			{/if}
		{/each}

		<div
			class="flex flex-col items-center space-y-4 text-center md:flex-row md:space-y-0 md:space-x-10 md:text-left"
		>
			<Button
				name="form-stepper-continue"
				label={isLastStep ? $_('button.start') : $_('button.continue')}
				{loading}
				type={ButtonType.submit}
			/>
			{#if isPreviousStep}
				<a class="text-base" href="#back" onclick={preventDefault(backStep)}>
					{$_('button.back')}
				</a>
			{/if}
		</div>
	</form>
{:else}
	{@render children?.()}
{/if}
